/**
 * What still has to be recorded in Quicken. Mileage never shows up as a bank
 * transaction, so you key it in yourself; everything else you tag once the card
 * charge lands. Recorded items stay listed (the most recent), so a mistaken
 * tick can be undone.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { card } from "@/components/atoms";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CategoryIcon, categoryIconKey } from "@/components/ExpenseIcons";
import { useToast } from "@/context/ToastContext";
import { pbError, useExpenseCategories, useExpenseMutations, useExpenseReports, useExpenses } from "@/hooks/useExpenses";
import { categoryNamer, dayOf, fmtDay, money } from "@/lib/expenses";
import type { Expense } from "@/lib/types";

import { ghostBtn, td } from "./ui";

const RECENT = 20;

export function Quicken() {
  const { data: expenses = [] } = useExpenses();
  const { data: reports = [] } = useExpenseReports();
  const { data: cats = [] } = useExpenseCategories();
  const { setFlag } = useExpenseMutations();
  const toast = useToast();
  const [bulk, setBulk] = useState<Expense[] | null>(null);

  const catName = useMemo(() => categoryNamer(cats), [cats]);
  const reportName = (id: string) => reports.find((r) => r.id === id)?.name ?? "";
  const all = useMemo(() => expenses.filter((e) => !e.parent).sort((a, b) => dayOf(b.date).localeCompare(dayOf(a.date))), [expenses]);
  const todo = all.filter((e) => !e.added_to_quicken);
  const manual = todo.filter((e) => e.is_mileage);
  const tag = todo.filter((e) => !e.is_mileage);
  const done = all.filter((e) => e.added_to_quicken);
  const sum = (l: Expense[]) => l.reduce((s, e) => s + (Number(e.amount) || 0), 0);

  async function mark(ids: string[], value: boolean) {
    try {
      await setFlag.mutateAsync({ ids, field: "added_to_quicken", value });
      toast(value ? (ids.length === 1 ? "Recorded" : `${ids.length} recorded`) : "Put back on the list");
    } catch (e) {
      toast("Couldn't update: " + pbError(e));
    }
  }

  const section = (title: string, hint: string, list: Expense[], allowBulk: boolean) => list.length > 0 && (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{title}</span>
        <span className="mono" style={{ fontSize: 12.5, color: "var(--muted)" }}>{money(sum(list))}</span>
        {allowBulk && (
          <button onClick={() => setBulk(list)} style={{ ...ghostBtn, marginLeft: "auto", padding: "5px 12px" }}>
            Mark all {list.length} recorded
          </button>
        )}
      </div>
      <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8 }}>{hint}</div>
      <div style={{ ...card, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            {list.map((e) => (
              <tr key={e.id}>
                <td style={{ ...td, width: 36 }}>
                  <input type="checkbox" checked={!!e.added_to_quicken} aria-label="Recorded in Quicken"
                    onChange={(ev) => void mark([e.id], ev.target.checked)} style={{ accentColor: "var(--accent)", width: 15, height: 15 }} />
                </td>
                <td style={{ ...td, whiteSpace: "nowrap", color: "var(--ink3)", width: 110 }}>{fmtDay(e.date)}</td>
                <td style={td}>
                  <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                    <span style={{ color: "var(--ink3)", display: "inline-flex" }}>
                      <CategoryIcon name={categoryIconKey(cats.find((c) => c.id === e.category) ?? { name: catName(e.category) })} size={16} />
                    </span>
                    <span>
                      {e.description || catName(e.category)}
                      {e.is_mileage && <span style={{ color: "var(--muted)", fontSize: 12 }}> · {e.miles || 0} mi</span>}
                    </span>
                  </div>
                </td>
                <td style={{ ...td, fontSize: 12.5 }}>
                  <Link to={`/expenses/${e.report}`} style={{ color: "var(--ink3)" }}>{reportName(e.report)}</Link>
                </td>
                <td style={{ ...td, textAlign: "right" }} className="mono">{money(e.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ fontSize: 12.5, color: "var(--muted)", marginBottom: 16 }}>
        {todo.length ? `${todo.length} item${todo.length === 1 ? "" : "s"} to record · ${money(sum(todo))}` : "Nothing waiting — every expense is in Quicken."}
      </div>
      {section("Enter manually", "Mileage never shows up as a bank transaction — key these in yourself.", manual, true)}
      {section("Tag when it clears", "These arrive as card transactions; tag them reimbursed as they land.", tag, true)}
      {section("Recorded", done.length > RECENT
        ? `The most recent ${RECENT} of ${done.length}. Untick one to put it back on the list.`
        : "Untick one to put it back on the list.", done.slice(0, RECENT), false)}

      {bulk && (
        <ConfirmDialog
          title={`Mark ${bulk.length} item${bulk.length === 1 ? "" : "s"} recorded?`}
          message="They move to Recorded, here and on your phone."
          confirmLabel="Mark recorded"
          danger={false}
          onConfirm={() => void mark(bulk.map((e) => e.id), true)}
          onClose={() => setBulk(null)}
        />
      )}
    </div>
  );
}
